/**
 * Barrel debris (plan 14 P14.3): thin-instanced shards over an upgraded layered explosion. Two
 * AUTHORED geometries from `BARREL_DIMS.shard` — a curved wall fragment (bung caps are small
 * scaled instances of it, never a third pool) and a lid ring — one thin-instance pool each, so a
 * peak detonation is exactly +2 draws and an idle frame is 0 (`thinInstanceCount` 0 draws
 * nothing). Every shard pose is a pure closed form f(age, seed, shardIndex): dragged ballistic
 * flight, ONE elastic bounce at deck level (piecewise, the bounce/rest times come from a
 * deterministic grid+bisection solve — pure f(inputs), never dt accumulation), tumbling ∝ a
 * hashed angular velocity, then rest → sink → fade. Same snapshot ⇒ same matrices, the
 * `burstParticle` purity rule.
 *
 * Shard lifetimes (DEBRIS_MAX_AGE ≈ 2.2 s) are tracked in THIS system's own explosion-event list
 * — not the fx ring — because they outlive RING_MAX_AGE; the list is events-capped with
 * deterministic oldest-event eviction.
 */
import { Matrix, Mesh, Quaternion, Vector3, VertexData } from '@babylonjs/core/pure';
import type { PBRMaterial, Scene } from '@babylonjs/core/pure';
import { hashValue } from './scene';
import type { RenderMaterials } from './scene';
import { toWorldX } from './actors';
import { BARREL_DIMS } from './barrelDims';
import { BRIDGE_HALF_WIDTH } from './world';
import type { SimEvent, SimState } from '../sim/sim';
import { BLASTS } from '../sim/level';
import type { LevelSpec } from '../sim/level';

/** A shard stops being an instance this long after its blast (fade tail ≈ the sink window). */
export const DEBRIS_MAX_AGE = 2.2;
/** This system's OWN event-list cap (plan 14 decision 3): ≈ 8 blasts, oldest evicted on overflow. */
export const DEBRIS_EVENT_CAP = 8;
/** Shards per explosion (the plan's 10–14 band), hashed across the two geometries. */
export const SHARDS_PER_BLAST = 12;

const DEBRIS_DRAG = 2.2;
const DEBRIS_G = 9.8;
/** Elastic-bounce restitution at the deck: one bounce, then the shard rests where it lands. */
const DEBRIS_RESTITUTION = 0.4;
/** Rest height on the deck, then the sink (metres/s) that fades the shard through the deck. */
const REST_Y = 0.03;
const SINK_DELAY_S = 0.45;
const SINK_RATE = 0.5;
/** Hashed shard mix: mostly wall fragments; bung caps are small-scaled wall instances. */
const WALL_SHARE = 0.72;
const BUNG_SHARE = 0.18;
const BUNG_SCALE = 0.32;

/**
 * The plan-09 "what you see is what hits you" scale rule, shared with the obstacle instance
 * matrix: the authored mesh's WIDEST extent (`hoopOuterDiameter`) spans the sim hit interval
 * `2 × toWorldX(halfWidth)`. Shards scale by the same factor, so a shard always reads as a piece
 * of THIS barrel at whatever size it exploded at.
 */
export function barrelWorldScale(halfWidth: number): number {
  return (2 * toWorldX(halfWidth)) / BARREL_DIMS.hoopOuterDiameter;
}

/** One explosion's shard field inputs: world origin, scale rule and blast reach, plus the seed. */
export interface ShardBlast {
  ox: number;
  oz: number;
  worldScale: number;
  /** Blast half-interval in world metres (`halfWidth × BRIDGE_HALF_WIDTH`): bounds shard reach. */
  reach: number;
  seed: number;
}

/** A shard's closed-form pose at `age` seconds after the blast. */
export interface ShardState {
  x: number;
  y: number;
  z: number;
  /** Uniform instance scale (authored units → world metres, × hashed variety). */
  scale: number;
  /** Tumble axis (unit) and angle (rad); the angle freezes when the shard rests. */
  ax: number;
  ay: number;
  az: number;
  angle: number;
  /** Which pool draws this shard: the wall fragment (bung caps included) or the lid ring. */
  wall: boolean;
  /** False outside [0, DEBRIS_MAX_AGE): no instance is written at all. */
  alive: boolean;
  /** True from the bounce's second deck contact on (rest → sink). */
  resting: boolean;
  /** Diagnostics computed by the closed form anyway: first deck contact, then rest. */
  bounceT: number;
  restT: number;
}

/** Dragged distance travelled along an axis with initial speed 1 (the burstParticle closed form). */
function dragged(k: number, t: number): number {
  return (1 - Math.exp(-k * t)) / k;
}

/**
 * First t ∈ [0, tMax] where fn(t) <= level (null if never): a 1/30 s grid scan brackets the
 * crossing, then 20 bisection halvings. Fixed iteration counts ⇒ deterministic and pure — the
 * same inputs always yield the same bounce/rest times, so a paused frame rebuilds bit-identical.
 */
function firstCrossing(fn: (t: number) => number, level: number, tMax: number): number | null {
  const steps = Math.max(4, Math.ceil(tMax * 30));
  const h = tMax / steps;
  let lo = 0;
  let hi = -1;
  for (let s = 1; s <= steps; s++) {
    if (fn(s * h) <= level) {
      hi = s * h;
      lo = (s - 1) * h;
      break;
    }
  }
  if (hi < 0) return null;
  for (let it = 0; it < 20; it++) {
    const mid = (lo + hi) / 2;
    if (fn(mid) <= level) hi = mid;
    else lo = mid;
  }
  return hi;
}

/**
 * Closed-form shard pose (the piecewise flight diagram of design decision 3):
 *   [0, bounceT)            dragged ballistic from the hashed launch state inside the barrel;
 *   [bounceT, restT)        the ONE elastic bounce — same closed form from the deck contact with
 *                          the vertical velocity reflected by DEBRIS_RESTITUTION;
 *   [restT, DEBRIS_MAX_AGE) rest (pose frozen), sink through the deck, gone at max age.
 * The horizontal drift is dragged-linear throughout and stops at rest; a shard that would fly
 * past the blast's x-interval is speed-capped by the event's own reach. False (not yet born /
 * dead) ⇒ no instance.
 */
export function shardPose(blast: ShardBlast, i: number, age: number, out: ShardState): boolean {
  if (!(age >= 0 && age < DEBRIS_MAX_AGE)) {
    out.alive = false;
    return false;
  }
  const { seed, worldScale, reach } = blast;
  // Bung caps are SMALL SCALED WALL-FRAGMENT instances (plan 14 decision 3: never a third pool).
  const bung = hashValue(i, seed, 41) < BUNG_SHARE;
  out.wall = bung || hashValue(i, seed, 40) < WALL_SHARE;
  const variety = 0.6 + 0.45 * hashValue(i, seed, 42);
  out.scale = worldScale * variety * (bung ? BUNG_SCALE : 1);
  const dir = hashValue(i, seed, 43) * Math.PI * 2;
  // Speed-capped so the FARTHEST shard (spawn offset + full dragged drift over its whole life)
  // stays inside the blast's own x-interval — what you see still frames what hits you.
  const barrelRadius = worldScale * BARREL_DIMS.hoopOuterDiameter * 0.5;
  const offMax = 0.35 * barrelRadius;
  const vMax = Math.max(1.6, (reach - offMax) / dragged(DEBRIS_DRAG, DEBRIS_MAX_AGE));
  const speed = 1.2 + (vMax - 1.2) * hashValue(i, seed, 44);
  const vx = Math.cos(dir) * speed;
  const vz = Math.sin(dir) * speed * 0.6; // the bridge runs on z: the scatter is mostly lateral
  const x0 = blast.ox + (hashValue(i, seed, 45) - 0.5) * 2 * offMax;
  const z0 = blast.oz + (hashValue(i, seed, 46) - 0.5) * 2 * offMax;
  const y0 = worldScale * BARREL_DIMS.height * (0.25 + 0.55 * hashValue(i, seed, 47));
  const vy = 2.0 + 3.5 * hashValue(i, seed, 48);
  const spin = (3 + 9 * hashValue(i, seed, 49)) * (hashValue(i, seed, 50) < 0.5 ? -1 : 1);
  let ax = hashValue(i, seed, 51) - 0.5;
  let ay = hashValue(i, seed, 52) - 0.5;
  let az = hashValue(i, seed, 53) - 0.5;
  const al = Math.hypot(ax, ay, az) || 1;
  ax /= al;
  ay /= al;
  az /= al;
  out.ax = ax;
  out.ay = ay;
  out.az = az;

  const k = DEBRIS_DRAG;
  const flight = (t: number): number => y0 + vy * dragged(k, t) - (DEBRIS_G / k) * (t - dragged(k, t));
  const vyAt = (t: number): number => vy * Math.exp(-k * t) - (DEBRIS_G / k) * (1 - Math.exp(-k * t));
  out.bounceT = firstCrossing(flight, REST_Y, DEBRIS_MAX_AGE) ?? DEBRIS_MAX_AGE;
  // The single elastic bounce: same closed form from the deck contact, vertical speed reflected.
  const bounceVy = -DEBRIS_RESTITUTION * Math.min(0, vyAt(out.bounceT));
  const arc2 = (u: number): number => REST_Y + bounceVy * dragged(k, u) - (DEBRIS_G / k) * (u - dragged(k, u));
  const uRest =
    out.bounceT >= DEBRIS_MAX_AGE ? Infinity : firstCrossing(arc2, REST_Y, DEBRIS_MAX_AGE - out.bounceT) ?? Infinity;
  out.restT = out.bounceT + uRest;

  const drift = dragged(k, Math.min(age, out.restT));
  out.x = x0 + vx * drift;
  out.z = z0 + vz * drift;
  if (age < out.bounceT) {
    out.y = flight(age);
    out.resting = false;
  } else if (age < out.restT) {
    out.y = arc2(age - out.bounceT);
    out.resting = false;
  } else {
    out.resting = true;
    out.y = REST_Y - SINK_RATE * Math.max(0, age - out.restT - SINK_DELAY_S);
  }
  out.angle = spin * Math.min(age, out.restT);
  out.alive = true;
  return true;
}

/** Strip between two concentric arcs extruded in y (open arcs get end caps): every shard shell. */
function arcStrip(
  rInner: number,
  rOuter: number,
  yBottom: number,
  yTop: number,
  arc: number,
  seg: number,
  caps: boolean,
): { positions: number[]; indices: number[] } {
  const positions: number[] = [];
  const indices: number[] = [];
  const vert = (theta: number, r: number, y: number): number => {
    positions.push(Math.sin(theta) * r, y, Math.cos(theta) * r);
    return positions.length / 3 - 1;
  };
  for (let j = 0; j < seg; j++) {
    const ta = (j / seg) * arc - arc / 2;
    const tb = ((j + 1) / seg) * arc - arc / 2;
    const a = vert(ta, rOuter, yBottom);
    const b = vert(tb, rOuter, yBottom);
    const c = vert(tb, rOuter, yTop);
    const d = vert(ta, rOuter, yTop);
    const e = vert(ta, rInner, yBottom);
    const f = vert(tb, rInner, yBottom);
    const g = vert(tb, rInner, yTop);
    const h = vert(ta, rInner, yTop);
    indices.push(a, b, c, a, c, d); // outer wall
    indices.push(f, e, h, f, h, g); // inner wall
    indices.push(d, c, g, d, g, h); // top
    indices.push(a, e, f, a, f, b); // bottom
    if (caps) {
      indices.push(a, d, h, a, h, e);
      indices.push(b, f, g, b, g, c);
    }
  }
  return { positions, indices };
}

/** Authored shard geometry (BARREL_DIMS units, centred on origin so the instance matrix tumbles it). */
function buildShardMesh(
  name: string,
  geo: { positions: number[]; indices: number[] },
  scene: Scene,
  material: PBRMaterial,
): Mesh {
  // Double-sided: tumbling shells must not show hole-through backfaces, and the shared obstacle
  // material culls backfaces (the +100% triangle cost is ≈ 150 tris/shard — noise in the budget).
  const base = geo.positions.length / 3;
  const positions = geo.positions.concat(geo.positions);
  const indices = geo.indices.slice();
  for (let k = 0; k < geo.indices.length; k += 3) {
    indices.push(geo.indices[k + 2]! + base, geo.indices[k + 1]! + base, geo.indices[k]! + base);
  }
  const vd = new VertexData();
  vd.positions = positions;
  vd.indices = indices;
  vd.normals = [];
  VertexData.ComputeNormals(vd.positions, vd.indices, vd.normals);
  const mesh = new Mesh(name, scene);
  vd.applyToMesh(mesh);
  mesh.material = material;
  mesh.isPickable = false;
  mesh.alwaysSelectAsActiveMesh = true; // instances move every frame; skip bound refresh
  return mesh;
}

/** One thin-instance draw: matrices rewritten per rebuild, tint written once. */
class ShardPool {
  readonly mesh: Mesh;
  readonly matrices: Float32Array;
  count = 0;
  constructor(mesh: Mesh, cap: number, tint: readonly [number, number, number]) {
    this.mesh = mesh;
    this.matrices = new Float32Array(cap * 16);
    const colors = new Float32Array(cap * 4);
    for (let i = 0; i < cap; i++) colors.set([tint[0], tint[1], tint[2], 1], i * 4);
    mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
    mesh.thinInstanceSetBuffer('color', colors, 4, false);
    this.begin();
    this.end();
  }
  begin(): void {
    this.count = 0;
  }
  end(): void {
    this.mesh.thinInstanceCount = this.count;
    this.mesh.setEnabled(this.count > 0);
    if (this.count > 0) this.mesh.thinInstanceBufferUpdated('matrix');
  }
}

/** One stamped explosion (the sim event, resolved to world space + the barrel's scale rule). */
interface DebrisEvent extends ShardBlast {
  t: number;
}

/**
 * The debris system: stamps `explosion` events into its own list, rebuilds both shard pools from
 * (events, sim.t). `maxEvents` is the tier cap — how many NEWEST blasts draw concurrently (the fx
 * pool-drop pattern); the list itself is capped at DEBRIS_EVENT_CAP with oldest-event eviction.
 */
export class Debris {
  private readonly wall: ShardPool;
  private readonly lid: ShardPool;
  private events: DebrisEvent[] = [];
  private readonly blastSpecs: { z: number; x: number; halfWidth: number }[];
  private readonly scratch: ShardState = {
    x: 0, y: 0, z: 0, scale: 1, ax: 0, ay: 1, az: 0, angle: 0, wall: true, alive: false, resting: false, bounceT: 0, restT: 0,
  };
  private readonly tmp = Matrix.Identity();
  private readonly quat = Quaternion.Identity();
  private readonly axis = new Vector3(0, 1, 0);
  private readonly scaleVec = new Vector3(1, 1, 1);
  private readonly pos = new Vector3();

  constructor(
    scene: Scene,
    materials: RenderMaterials,
    /** The active level: the shard scale rule reads the BLASTS-kind specs this level actually ships. */
    level: LevelSpec,
    private readonly maxEvents: number,
  ) {
    this.blastSpecs = level.obstacles
      .filter((o) => BLASTS[o.kind] !== undefined)
      .map((o) => ({ z: o.z, x: o.x, halfWidth: o.halfWidth }));
    const s = BARREL_DIMS.shard;
    const wallGeo = arcStrip(
      BARREL_DIMS.bodyDiameter / 2 - s.wallThickness,
      BARREL_DIMS.bodyDiameter / 2,
      (-s.wallHeightFraction * BARREL_DIMS.height) / 2,
      (s.wallHeightFraction * BARREL_DIMS.height) / 2,
      s.wallArc,
      6,
      true,
    );
    const lidGeo = arcStrip(
      (s.lidInnerFraction * BARREL_DIMS.lidDiameter) / 2,
      BARREL_DIMS.lidDiameter / 2,
      -s.lidThickness / 2,
      s.lidThickness / 2,
      Math.PI * 2,
      16,
      false,
    );
    this.wall = new ShardPool(buildShardMesh('debris-wall', wallGeo, scene, materials.metal), maxEvents * SHARDS_PER_BLAST, BARREL_DIMS.tint);
    this.lid = new ShardPool(buildShardMesh('debris-lid', lidGeo, scene, materials.metal), maxEvents * SHARDS_PER_BLAST, BARREL_DIMS.tint);
  }

  /** Stamp this frame's drained sim `explosion` events (they carry the sim blast interval). */
  handleEvents(events: SimEvent[]): void {
    for (const ev of events) {
      if (ev.type !== 'explosion') continue;
      // The explosion event carries the BLAST interval; the shard scale needs the exploded
      // obstacle's own hit halfWidth, resolved against this level's BLASTS specs at the event's exact (z, x)
      // (the sim stamps both from the obstacle). A mismatch is a sim/render data drift — stamping
      // anyway (z-only match, first barrel) would silently draw wrong-scale shards and hide it,
      // so an exact miss is skipped instead.
      const spec = this.blastSpecs.find((b) => b.z === ev.z && b.x === ev.x);
      if (!spec) continue;
      this.events.push({
        t: ev.t,
        ox: toWorldX(ev.x),
        oz: ev.z,
        worldScale: barrelWorldScale(spec.halfWidth),
        reach: ev.halfWidth * BRIDGE_HALF_WIDTH,
        // t alone would seed CHAINED barrels identically (breakObstacle resolves a chain in one
        // sim step, so B and C share t): mix the event's geometry in, still deterministic.
        seed: (Math.round(ev.t * 1000) ^ (Math.round(ev.x * 100) << 8) ^ (ev.z << 16)) | 0,
      });
      // Prune dead blasts, then cap by evicting the OLDEST event (deterministic, the purity rule).
      this.events = this.events.filter((e) => ev.t - e.t < DEBRIS_MAX_AGE);
      if (this.events.length > DEBRIS_EVENT_CAP) this.events = this.events.slice(-DEBRIS_EVENT_CAP);
    }
  }

  /** Restart: drop the event list and every instance (the fx.reset precedent). */
  reset(): void {
    this.events = [];
    for (const pool of [this.wall, this.lid]) {
      pool.begin();
      pool.end();
    }
  }

  /** Rebuild both pools from the snapshot + event list. Pure: same inputs, same matrices. */
  update(sim: SimState): void {
    this.wall.begin();
    this.lid.begin();
    // Newest first, so a tier pool smaller than the live blasts drops the oldest (the fx pattern).
    let blasts = 0;
    for (let e = this.events.length - 1; e >= 0 && blasts < this.maxEvents; e--) {
      const ev = this.events[e]!;
      const age = sim.t - ev.t;
      if (!(age >= 0 && age < DEBRIS_MAX_AGE)) continue;
      blasts++;
      for (let i = 0; i < SHARDS_PER_BLAST; i++) {
        if (!shardPose(ev, i, age, this.scratch)) continue;
        const pool = this.scratch.wall ? this.wall : this.lid;
        if (pool.count >= pool.matrices.length / 16) continue;
        this.axis.set(this.scratch.ax, this.scratch.ay, this.scratch.az);
        Quaternion.RotationAxisToRef(this.axis, this.scratch.angle, this.quat);
        this.scaleVec.setAll(this.scratch.scale);
        this.pos.set(this.scratch.x, this.scratch.y, this.scratch.z);
        Matrix.ComposeToRef(this.scaleVec, this.quat, this.pos, this.tmp);
        this.tmp.copyToArray(pool.matrices, pool.count * 16);
        pool.count++;
      }
    }
    this.wall.end();
    this.lid.end();
  }

  /** Probe/test: live shard instances per pool. */
  debugCounts(): { wall: number; lid: number } {
    return { wall: this.wall.count, lid: this.lid.count };
  }

  /** Test handle: a copy of one pool's written matrices. */
  debugMatrices(kind: 'wall' | 'lid'): number[] {
    const pool = this[kind];
    return Array.from(pool.matrices.subarray(0, pool.count * 16));
  }

  /** Test handle: live entries in this system's own event list. */
  debugEvents(): number {
    return this.events.length;
  }
}
